import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  cacheKeys,
  useIsFeatureEnabled,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AssetHeader,
  Colors,
  LoadingIndicator,
  SlideAction,
  getAutoPromptProps,
  useResponsiveDesign,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useAssetRouting } from '../assetRouteContext';
import { RoutineSubmitSource, isActivityOrEditPage } from '../assetUtils';
import { AssetActivityFold } from './assetActivityFold';
import { AssetCard as AssetCardOld } from './assetCard-old';
import { AssetMediaFold } from './assetMediaFold';
import { AssetRoutineFold } from './assetRoutineFold';

export const AssetCard = ({
  assetId,
  forceShowing,
}: {
  assetId: string;
  forceShowing?: boolean;
}) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <AssetCardBase assetId={assetId} forceShowing={forceShowing} />;
  }

  return <AssetCardOld assetId={assetId} />;
};

export const AssetCardBase = ({ assetId, forceShowing }: { assetId: string; forceShowing?: boolean }) => {
  const { onSlideChange, setColumnTitle } = useSlideLayout();
  const location = useLocation();
  const { basePath, isWorkspaceMode } = useAssetRouting(assetId);
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>(
    undefined
  );
  const { t } = useTranslation();
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery: invalidateWorkspaceAsset,
  } = useWorkspaceAsset(assetId);
  const navigate = useNavigate();
  const isResponsiveDesign = useResponsiveDesign();
  const queryClient = useQueryClient();
  const isEditPage = isActivityOrEditPage(location);
  const isWeb = Platform.OS === 'web';

  // Reset component state when assetId changes
  useEffect(() => {
    invalidateWorkspaceAsset();
  }, [assetId]);

  // Auto-prompt routines on asset open for mobile only
  useEffect(() => {
    if (isWeb || isLoadingWorkspaceAsset || !workspaceAsset) {
      return;
    }

    if (workspaceAsset.asset?.id !== assetId) {
      return;
    }

    if (!isEditPage) {
      const autoPrompt = getAutoPromptProps(workspaceAsset);

      if (autoPrompt) {
        navigate(
          `/assets/${assetId}/routines/${autoPrompt.first.routine?.id}`,
          { state: { from: RoutineSubmitSource.AssetCard } }
        );
      }
    }
  }, [isLoadingWorkspaceAsset, workspaceAsset, assetId, isEditPage]);

  useEffect(() => {
    if (workspaceAsset) {
      setColumnTitle(workspaceAsset.asset?.name || 'assets.menu.title');
    }
  }, [workspaceAsset, setColumnTitle]);

  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);

  const onIssueSelected = async (issueId: string) => {
    setSelectedIssueId(issueId);

    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`${basePath}/activity/${issueId}`, { state: { from: RoutineSubmitSource.AssetCard } });
  };

  const onAssetEdit = async () => {
    if (!isActivityOrEditPage(location)) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`${basePath}/edit`);
  };

  const viewAssetInfoLink = !location.pathname.includes('edit') ? (
    <Pressable style={styles.viewAssetButton} onPress={onAssetEdit}>
      <Text style={styles.viewAssetButtonLabel}>
        {`${t(
          isWorkspaceMode ? 'topNav.moreWorkspaceInfo' : 'topNav.moreAssetInfo'
        )}`}
      </Text>
      <MaterialCommunityIcons
        size={24}
        color={Colors.primaryDark}
        name="chevron-right"
        style={{
          marginLeft: -4,
          textAlign: 'justify',
        }}
      />
    </Pressable>
  ) : null;

  if (isLoadingWorkspaceAsset || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  return (
    workspaceAsset !== undefined && (
      <View style={styles.container}>
        <View style={styles.containerHeader}>
          <AssetHeader
            workspaceAsset={workspaceAsset!}
            additionalHeaderBlock={viewAssetInfoLink}
            assetDetailsContainerStyle={styles.assetDetailsContainerStyle}
            imgSize={isResponsiveDesign ? 85 : 112}
            invalidateQuery={async () => await invalidateWorkspaceAsset()}
            mediaPicker={MediaPicker}
          />
        </View>
        <AssetRoutineFold assetId={assetId} forceShowing={forceShowing} />
        <AssetActivityFold
          assetId={assetId}
          onIssueSelected={onIssueSelected}
          selectedIssue={selectedIssueId}
          showAssetName={false}
          forceShowing={forceShowing}
        />
        <AssetMediaFold workspaceAsset={workspaceAsset} assetId={assetId} forceShowing={forceShowing} />
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerHeader: {
    paddingTop: 6,
    paddingHorizontal: 16,
    backgroundColor: Colors.brand.white,
  },
  assetDetailsContainerStyle: {
    height: 112,
    justifyContent: 'space-between',
  },
  viewAssetButton: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 24,
    marginTop: 5,
    paddingVertical: 0,
    marginVertical: 0,
  },
  viewAssetButtonLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.primaryDark,
    lineHeight: 19,
    marginRight: 0,
    padding: 0,
  },
});
